import React, { useEffect, useRef, useState } from 'react';
import {
  BrowserRouter as Router,
  Routes, // instead of "Switch"
  Route,
  useLocation,
  Navigate,
  useNavigationType,
} from "react-router-dom";
import LandingPage from './components/auth/LandingPage';
import DashboardContainer from './components/dashboard/site_dashboard/DashboardContainer';
import RMISNavbar from './components/navigation/RMISNavbar';
import Footer from './components/navigation/Footer';
import ClientGraphContainer from './components/metric_graphs/utilities/raw_data/components/client_version/ClientGraphContainer';
import './App.css'
import InfographicContainer from './components/test/infographics/InfographicContainer';
import PersistLogin from './components/PersistLogin';
import useAuth from './hooks/useAuth';
import EmissionsDashboard from './components/emissions/EmissionsDashboard';
import GHGFactors from './components/staff_pages/ghg_factors/GHGFactors';
import OrgManagementPage from './components/dashboard/staff_dashboard/OrgManagementPage';
import SiteManagementPage from './components/dashboard/staff_dashboard/SiteManagementPage';
import UserAccountPage from './components/dashboard/staff_dashboard/settings/UserAccountsPage';
import ProfilePage from './components/auth/ProfilePage';

import { createContext } from 'react';
import ScrollToTop from './components/navigation/ScrollToTop';
import MaintenanceContainer from './components/maintenance/MaintenanceContainer';
import OpportunityContainer from './components/opportunities/OpportunityContainer';
import ReportContainer from './components/reporting/ReportContainer';
import UserAgreement from './components/global/UserAgreement';
import ProjectManagementContainer from './components/implementation/ProjectManagementContainer';

import { generateClientCrumbs, generateStaffCrumbs, staffCrumbs } from './breadcrumbData';
import { isMobile } from 'react-device-detect';
import MobileLandingPage from './mobile/MobileLandingPage';
import DashboardRouter from './components/dashboard/DashboardRouter';
import OpportunityListContainer from './components/opportunities/OpportunityListContainer';
import SetPassword from './components/auth/SetPassword';
import PricingContainer from './components/pricing/PricingContainer';
import EventsContainer from './components/events/EventsContainer';
import BlueRoofContainer from './components/blueroof/BlueRoofContainer';
import ActivityLogContainer from './components/activity_log/ActivityLogContainer';
import CustomerFeedback from './components/navigation/CustomerFeedback';
import CustomerManagementContainer from './components/customer_management/CustomerManagementContainer';
import HMartContainer from './components/hmart/HMartContainer';
import Spirax from './components/spirax/Spirax';
import AlertsContainer from './components/alerts/AlertsContainer';
import Overview from './components/dashboard/client_dashboard/client_components/Overview';
import UtilityTrendContainer from './components/staff_pages/utility_data/utility_trend_system/UtilityTrendContainer';
import useAxiosProtect from './hooks/useAxiosProtect';
import SiteOppList from './mobile/SiteOppList';
import AdminTablesContainer from './components/admin_tables/AdminTablesContainer';
export const ThemeContext = createContext("light");

const App = () => {
  // dynamic rendering and responsive states
  const [isPortrait, setIsPortrait] = useState(false);
  const {auth} = useAuth();

  const axiosProtect = useAxiosProtect();
  const canViewInternalSites = auth?.role != null && (
    auth.role === process.env.REACT_APP_ES_ADMIN || auth.role === process.env.REACT_APP_ES_USER
  );

  const [userAgreementOpen, setUserAgreementOpen] = useState(false);

  const [currentPath, setCurrentPath] = useState([staffCrumbs['home']]);

  const [userData, setUserData] = useState(undefined); // use to access all auth token data

  const [unreadAlerts, setUnreadAlerts] = useState(0);

  const checkTOSCompliance = () => {
    if(window.location.pathname === '/' || Object.keys(auth).length === 0) return;
    if(auth?.agreed_to_tos === false) setUserAgreementOpen(true); 
  }

  useEffect(() => {
    const setPortraitState = () => {
      setIsPortrait(window.innerHeight > window.innerWidth);
    }

    window.addEventListener('resize', setPortraitState);

    return () => {
      window.removeEventListener('resize', setPortraitState);
    }
  }, []);

  const location = useLocation();
  const navType = useNavigationType();

  //auto-scroll back to top whenever user navigates
  useEffect(() => {
    if(location.hash) return;
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });

  }, [location.pathname, location.search, location.hash, navType]);

  //suppress browser auto-scroll restoration from prev pages
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      const prev = window.history.scrollRestoration;
      window.history.scrollRestoration = 'manual';
      return () => { window.history.scrollRestoration = prev; };
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!auth) return;

      const segments = location.pathname.split('/').filter(Boolean);

      const isStaff =
        parseInt(auth?.role) >= parseInt(process.env.REACT_APP_ES_USER) &&
        parseInt(auth?.role) <= parseInt(process.env.REACT_APP_ES_ADMIN);

      // rebuild crumbs each time
      const extra = isStaff ? await generateStaffCrumbs(segments) : await generateClientCrumbs(segments);

      if (cancelled) return;

      const home = { ...staffCrumbs.home };
      setCurrentPath([home, ...(extra || [])]);

      if (!isStaff) checkTOSCompliance();
    }

    run();
    return () => { cancelled = true; };
  }, [location.key, location.pathname, auth?.role]); 

  useEffect(() => {
    if(auth == null) return;
    setUserData(auth);
    
    if(Object.keys(auth).length === 0) return;

    const isStaff = parseInt(auth?.role) >= parseInt(process.env.REACT_APP_ES_USER) && parseInt(auth?.role) <= parseInt(process.env.REACT_APP_ES_ADMIN);

    if(!isStaff){
      checkTOSCompliance();
    }

    axiosProtect.get(`/a/alerts/count-unread?uid=${auth?.id}`).then(response => {
      setUnreadAlerts(response.data.unreadAlerts);
    }).catch(() => {});

  }, [auth?.role, auth?.agreed_to_tos, location.key]);

  const [theme, setTheme] = useState("light")

  const toggleTheme = () => {
    setTheme((curr) => (curr === "light" ? "dark" : "light"));
  }

  return (
    <ThemeContext.Provider value={{theme, toggleTheme}}>
      <div className="App" id={theme}>

      <UserAgreement userAgreementOpen={userAgreementOpen} setUserAgreementOpen={setUserAgreementOpen} auth={auth}/>

      {(location.pathname !== '/' && !location.pathname.startsWith('/report')) 
      && !location.pathname.startsWith('/infographics') && !location.pathname.startsWith('/activate') && !location.pathname.startsWith('/reset') 
      && <RMISNavbar currentPath={currentPath} unreadAlerts={unreadAlerts}/>}

      <ScrollToTop/>

      {
        ((
            auth?.role === process.env.REACT_APP_CLIENT_ADMIN || 
            auth?.role === process.env.REACT_APP_CLIENT_STAFF || 
            auth?.role === process.env.REACT_APP_ES_USER || 
            auth?.role === process.env.REACT_APP_ES_ADMIN
        ) && location.pathname !== '/') && <CustomerFeedback/>
      }

      {
        isMobile ? (
          <Routes>
            <Route element={<PersistLogin/>}>

              {/* mobile-friendly allowlist: */}

              {/* ---- UNCOMMENT THE FOLLOWING FOR MOBILE DEV --- */}

              <Route path='/' element={<LandingPage isPortrait={isPortrait}/>}/>

              {
                (
                  auth?.role === process.env.REACT_APP_CLIENT_ADMIN || 
                  auth?.role === process.env.REACT_APP_CLIENT_STAFF || 
                  auth?.role === process.env.REACT_APP_ES_USER || 
                  auth?.role === process.env.REACT_APP_ES_ADMIN
                )
                &&
                <>
                <Route path='/dashboard' element={
                  <DashboardRouter userData={userData} auth={auth} isPortrait={isPortrait} isMobile={isMobile}/>
                }/>

                <Route path='/alerts/*' element={<AlertsContainer isPortrait={isPortrait}/>}/>
                <Route path='/energy-metrics/*' element={<ClientGraphContainer isPortrait={isPortrait} userData={userData}/>}/>
                <Route path='/site-opportunities/*' element={<SiteOppList isPortrait={isPortrait} userData={userData}/>}/>
                </>
              }

              {/* --- END OF SECTION TO UNCOMMENT ---*/}

              {/* Else route to blocked page: */}
              {/* <Route path='*' element={<MobileLandingPage/>}/> */}
            </Route>
          </Routes>
        ) : (
          <Routes>
            <Route element={<PersistLogin/>}>
              <Route path='/' element={<LandingPage isPortrait={isPortrait}/>}/>

              <Route path='/dashboard' element={
                // auth?.role === process.env.REACT_APP_CLIENT_ADMIN || auth?.role === process.env.REACT_APP_CLIENT_STAFF ?  <ClientDashContainer isPortrait={isPortrait}/> 
                // : auth?.role === process.env.REACT_APP_ES_ADMIN || auth?.role === process.env.REACT_APP_ES_USER ? <StaffDashContainer isPortrait={isPortrait}/> :
                // <Navigate to='/' replace />
                <DashboardRouter userData={userData} auth={auth} isPortrait={isPortrait}/>
              }/>

              <Route path='/ghg-factors' element={
                auth?.role === process.env.REACT_APP_ES_ADMIN || auth?.role === process.env.REACT_APP_ES_USER ? <GHGFactors isPortrait={isPortrait}/> :
                <Navigate to='/' replace />
              }/>

              <Route path='/client/*' element={
                auth?.role === process.env.REACT_APP_ES_ADMIN || auth?.role === process.env.REACT_APP_ES_USER ? <OrgManagementPage isPortrait={isPortrait}/> :
                <Navigate to='/' replace /> // possibly change from redirect to client's version of page once created
              }/>

              <Route path='/sites/:siteId/*' element={
                canViewInternalSites ? <SiteManagementPage isPortrait={isPortrait} userData={userData}/> :
                <Navigate to='/' replace /> // possibly change from redirect to client's version of page once created
              }/>

              <Route path='/pricing/*' element={
                auth?.role === process.env.REACT_APP_ES_ADMIN || auth?.role === process.env.REACT_APP_ES_USER ? <PricingContainer isPortrait={isPortrait}/> :
                <Navigate to='/' replace /> // possibly change from redirect to client's version of page once created
              }/>

              <Route path='/blueroof/*' element={
                auth?.role === process.env.REACT_APP_ES_ADMIN || auth?.role === process.env.REACT_APP_ES_USER || auth?.role === process.env.REACT_APP_BLUE_ROOF_CLIENT ? <BlueRoofContainer isPortrait={isPortrait} auth={auth}/> :
                <Navigate to='/' replace /> // possibly change from redirect to client's version of page once created
              }/>

              <Route path='/customer-management/*' element={
                auth?.role === process.env.REACT_APP_ES_ADMIN || auth?.role === process.env.REACT_APP_ES_USER ? <CustomerManagementContainer isPortrait={isPortrait}/> :
                <Navigate to='/' replace /> // possibly change from redirect to client's version of page once created
              }/>

              <Route path='/hmart' element={
                auth?.role === process.env.REACT_APP_ES_ADMIN || auth?.role === process.env.REACT_APP_ES_USER ? <HMartContainer isPortrait={isPortrait}/> :
                <Navigate to='/' replace /> // possibly change from redirect to client's version of page once created
              }/>

              <Route path='/admin-tables/*' element={
                auth?.role === process.env.REACT_APP_ES_ADMIN || auth?.role === process.env.REACT_APP_ES_USER ? <AdminTablesContainer isPortrait={isPortrait} auth={auth}/> :
                <Navigate to='/' replace />
              }/>

              {/* <Route path='/guest' element={<DashboardContainer isPortrait={isPortrait}/>}/> */}
              <Route path='/site/*' element={<DashboardContainer isPortrait={isPortrait}/>}/>
              <Route path='/energy-metrics/*' element={<ClientGraphContainer isPortrait={isPortrait} userData={userData}/>}/>
              <Route path='/opportunities' element={<OpportunityListContainer isPortrait={isPortrait} userData={userData}/>}/>
              <Route path='/opportunities/*' element={<OpportunityContainer isPortrait={isPortrait}/>}/>
              {/* <Route path='/opportunities-guest' element={<Container isPortrait={isPortrait}/>}/> */}
              <Route path='/infographics/*' element={<InfographicContainer isPortrait={isPortrait}/>}/>
              
              {/* <Route path='/emissions' element={<EmissionsDashboard isPortrait={isPortrait} userData={userData}/>}/> */}
              <Route path='/emissions-savings' element={<EmissionsDashboard isPortrait={isPortrait} userData={userData} page='Overview'/>}/>
              <Route path='/emissions-trends' element={<EmissionsDashboard isPortrait={isPortrait} userData={userData} page='Trends'/>}/>
              
              <Route path='/maintenance' element={<MaintenanceContainer isPortrait={isPortrait}/>}/>
              <Route path='/report/*' element={<ReportContainer isPortrait={isPortrait}/>}/>
              <Route path='/activate/*' element={<SetPassword isPortrait={isPortrait}/>}/>
              <Route path='/reset/*' element={<SetPassword isPortrait={isPortrait}/>}/>

              <Route path='/projects/*' element={<ProjectManagementContainer isPortrait={isPortrait}/>}/>
              <Route path='/schedule/*' element={<EventsContainer isPortrait={isPortrait}/>}/>

              <Route path='/activity-log/*' element={<ActivityLogContainer isPortrait={isPortrait}/>}/>

              <Route path='/alerts/*' element={<AlertsContainer isPortrait={isPortrait}/>}/>

              <Route path='/overview' element={<Overview isPortrait={isPortrait} userData={userData}/>}/>

              <Route path='/utility-trend' element={<UtilityTrendContainer isPortrait={isPortrait} clientView={true}/>}/>
              {/* <Route path='/overview/*' element={<OverviewContainer isPortrait={isPortrait} />}/> */}
              
              {/* <Route path='/staff' element={<StaffDashContainer isPortrait={isPortrait}/>}/> */}
              
              <Route path='/user-accounts' element={<UserAccountPage isPortrait={isPortrait}/>}/>
              <Route path='/profile' element={<ProfilePage isPortrait={isPortrait}/>}/>
              <Route path='/spirax' element={<Spirax isPortrait={isPortrait}/>}/>

              {/* <Route path='/demo-data' element={<ExampleGraph isPortrait={isPortrait}/>}/> */}
              
            </Route>
          </Routes>
        )
      }
      {(location.pathname !== '/' && !location.pathname.startsWith('/report')) 
      && !location.pathname.startsWith('/infographics') && !location.pathname.startsWith('/activate') && !location.pathname.startsWith('/reset') 
      && <Footer/>}
      </div>
    </ThemeContext.Provider>
  );
}

export default App;